import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styles from './PublicPaymentPage.module.css';
import DataFallback from '../components/feedback/DataFallback';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function PublicPaymentPage() {
  const { linkToken } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Customer form data
  const [customerData, setCustomerData] = useState({
    customer_name: '',
  });

  const fetchPaymentLinkDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/payment-links/${linkToken}/`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Payment link not found or has expired.');
        } else if (response.status === 410) {
          setError('This payment link has already been used.');
        } else {
          setError('Failed to load payment details. Please try again.');
        }
        return;
      }
      
      const data = await response.json();
      
      if (data.status !== 'success' || !data.payment_link) {
        setError('This payment link is no longer valid.');
        return;
      }
      
      // Extract payment link data from the response
      const paymentLinkData = data.payment_link;
      setPaymentData(paymentLinkData);
      
      // Pre-fill customer data if available
      if (paymentLinkData.customer_email || paymentLinkData.customer_name) {
        setCustomerData(prev => ({
          ...prev,
          customer_name: paymentLinkData.customer_name || '',
        }));
      }
      
    } catch (err) {
      console.error('Error fetching payment link:', err);
      setError('Failed to load payment details. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  }, [linkToken]);

  useEffect(() => {
    fetchPaymentLinkDetails();
  }, [linkToken, fetchPaymentLinkDetails]);

  const handleInputChange = (field, value) => {
    // Only allow updating customer_name (which is disabled, so this is a no-op)
    if (field === 'customer_name') {
      setCustomerData(prev => ({
        ...prev,
        customer_name: value
      }));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!customerData.customer_name.trim()) {
      setError('Please enter your name.');
      return;
    }
    
    try {
      setProcessing(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/payment-links/${linkToken}/pay/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_name: customerData.customer_name }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to initiate payment. Please try again.');
        return;
      }
      
      const result = await response.json();
      
      if (result.status === 'success' && result.payment_data) {
        // Submit form to PayU
        submitToPayU(result);
      } else {
        setError('Failed to prepare payment. Please try again.');
      }
      
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const submitToPayU = (paymentResponse) => {
    // Create a form and submit to PayU
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentResponse.payment_url;
    
    Object.keys(paymentResponse.payment_data).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = paymentResponse.payment_data[key];
      form.appendChild(input);
    });
    
    document.body.appendChild(form);
    form.submit();
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingCard}>
          <div className={styles.loadingSpinner}></div>
          <h2>Loading payment details...</h2>
          <p>Please wait while we fetch your payment information.</p>
        </div>
      </div>
    );
  }

  if (error || !paymentData) {
    // The most exposed screen in the product: unauthenticated, and seen by a
    // brand's own customers rather than by Terminal users. "Payment Link Error"
    // beside a raw message told a shopper their link was broken when the usual
    // cause is that our server was briefly unreachable.
    return (
      <div className={styles.container}>
        <DataFallback
          variant="pane"
          error={typeof error === 'string' ? { message: error, retryable: true } : error}
          onRetry={() => window.location.reload()}
        >
          <p style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '0.5rem' }}>
            If this keeps happening, please contact the brand directly.
          </p>
        </DataFallback>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.paymentCard}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.brandInfo}>
            <h1>{paymentData.brand.name}</h1>
            {paymentData.brand.description && (
              <p className={styles.brandDescription}>{paymentData.brand.description}</p>
            )}
          </div>
          <div className={styles.securityBadge}>
            <span>🔒 Secure Payment</span>
          </div>
        </div>

        {/* Plan Details */}
        <div className={styles.planSection}>
          <h2>Subscription Plan</h2>
          <div className={styles.planCard}>
            <div className={styles.planHeader}>
              <h3>{paymentData.plan.name}</h3>
              <div className={styles.planPrice}>
                {formatCurrency(paymentData.amount, paymentData.currency)}
                <span className={styles.priceFrequency}>/month</span>
              </div>
            </div>
            
            {paymentData.plan.features && paymentData.plan.features.length > 0 && (
              <div className={styles.planFeatures}>
                <h4>Included Features:</h4>
                <ul>
                  {paymentData.plan.features.map((feature, index) => (
                    <li key={feature.id || index}>✓ {feature.name || feature}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Customer Form */}
        <form onSubmit={handlePayment} className={styles.customerForm}>
          <h3>Your Details</h3>
          
          <div className={styles.formGroup}>
            <label htmlFor="customer_name">Full Name *</label>
            <input
              type="text"
              id="customer_name"
              value={customerData.customer_name}
              onChange={(e) => handleInputChange('customer_name', e.target.value)}
              placeholder="Enter your full name"
              required
              disabled
              className={styles.disabledInput}
            />
          </div>

          {paymentData.customer_email && (
            <div className={styles.formGroup}>
              <label>Email Address</label>
              <input
                type="email"
                value={paymentData.customer_email}
                disabled
                className={styles.disabledInput}
              />
              <small>This email was provided when the payment link was created.</small>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <div className={styles.paymentSummary}>
            <div className={styles.summaryRow}>
              <span>Subscription Plan:</span>
              <span>{paymentData.plan.name}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Brand:</span>
              <span>{paymentData.brand.name}</span>
            </div>
            <div className={styles.summaryRowTotal}>
              <span>Total Amount:</span>
              <span>{formatCurrency(paymentData.amount, paymentData.currency)}</span>
            </div>
          </div>

          <button 
            type="submit" 
            className={styles.payButton}
            disabled={processing}
          >
            {processing ? (
              <>
                <div className={styles.buttonSpinner}></div>
                Processing...
              </>
            ) : (
              <>
                🔒 Pay {formatCurrency(paymentData.amount, paymentData.currency)}
              </>
            )}
          </button>

          <div className={styles.securityNote}>
            <p>
              <strong>Secure Payment:</strong> You will be redirected to PayU's secure payment gateway. 
              Your payment information is protected and encrypted.
            </p>
          </div>
        </form>

        {/* Expiry Warning */}
        {paymentData.expiry_date && (
          <div className={styles.expiryWarning}>
            <p>
              ⏰ This payment link expires on{' '}
              <strong>{new Date(paymentData.expiry_date).toLocaleDateString('en-IN', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicPaymentPage;
